(() => {
  const t = (i18n, key, fallback) =>
    (i18n && i18n[key] != null && String(i18n[key]).trim() !== '' && !String(i18n[key]).startsWith('translation missing'))
      ? i18n[key]
      : fallback;

  const cartAddUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}cart/add.js`;
  };

  const norm = (s) =>
    String(s || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/×/g, 'x')
      .replace(/\s+/g, ' ')
      .trim();

  const includesAny = (hay, needles) => {
    if (!needles || !needles.length) return false;
    const h = norm(hay);
    return needles.some((n) => h.includes(norm(n)));
  };

  const excludesAny = (hay, needles) => {
    if (!needles || !needles.length) return true;
    const h = norm(hay);
    return !needles.some((n) => h.includes(norm(n)));
  };

  const formatMoney = (cents) => {
    if (cents == null || cents === '') return '—';
    const n = Number(cents);
    if (Number.isNaN(n)) return '—';
    const whole = Math.round(n / 100);
    return `${whole.toLocaleString('tr-TR')} ₺`;
  };

  const moneyOf = (variant) => {
    if (!variant) return '—';
    return formatMoney(variant.price);
  };

  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

  const notifyHorizonCart = async (addResponse, lines) => {
    let cartJson = null;
    try {
      cartJson = await fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());
    } catch (_) {}

    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines || [],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: cartJson ? CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson) : undefined,
        detail: {
          items: cartJson?.items || [],
          source: 'standbox-craft',
          itemCount: cartJson?.item_count,
          sections: addResponse?.sections || {},
          didError: false,
        },
      });
    } catch (err) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cartJson || addResponse, bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartJson || addResponse, bubbles: true }));
    }
  };

  class StandboxCraft extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const cfgEl = document.getElementById(this.dataset.configId || '');
      if (!cfgEl) return;
      try {
        this.config = JSON.parse(cfgEl.textContent);
      } catch (e) {
        console.error('[StandboxCraft] bad config', e);
        return;
      }

      this.i18n = this.config.i18n || {};
      this.variants = this.config.variants || [];
      this.option = this.config.option || {};
      this.media = this.config.media || [];
      this.moneyFormat = this.config.moneyFormat || '{{amount}} ₺';
      this.printEnabled = this.config.printEnabled !== false;
      this.productTitle = this.config.productTitle || '';
      this.wantPrint = false;
      this.printSource = 'upload';
      this.printNote = '';
      this.fileFront = null;
      this.fileBack = null;
      this._frontUrl = null;
      this._backUrl = null;

      this._cacheDom();
      this._bind();
      if (this.printEnabled) this._placePrintAfterCtas();
      this._markHosts();
      this._interceptCart();
      this._styleCtas();
      this._initGalleryUi();
      this._sync();
    }

    disconnectedCallback() {
      if (this._frontUrl) URL.revokeObjectURL(this._frontUrl);
      if (this._backUrl) URL.revokeObjectURL(this._backUrl);
      this._teardownGalleryUi();
    }

    _cacheDom() {
      this.printBtn = this.querySelector('[data-sb-print-btn]');
      this.printMeta = this.querySelector('[data-sb-print-meta]');
      this.printPriceEl = this.querySelector('[data-sb-print-price]');
      this.uploadEl = this.querySelector('[data-sb-upload]');
      this.printGuideEl = this.querySelector('[data-sb-print-guide]');
      this.fileFrontInput = this.querySelector('[data-sb-file-front]');
      this.fileBackInput = this.querySelector('[data-sb-file-back]');
      this.fileFrontName = this.querySelector('[data-sb-file-front-name]');
      this.fileBackName = this.querySelector('[data-sb-file-back-name]');
      this.printWarning = this.querySelector('[data-sb-print-warning]');
      this.printStepEl = this.querySelector('[data-sb-print-step]');
      this.uploadFilesEl = this.querySelector('[data-sb-upload-files]');
      this.serviceNoteEl = this.querySelector('[data-sb-service-note]');
      this.printNoteEl = this.querySelector('[data-sb-print-note]');
      this.sourceInputs = Array.from(this.querySelectorAll('[data-sb-source]'));
      this.headlineEl = this.querySelector('[data-sb-headline]');
      this.descEl = this.querySelector('[data-sb-desc]');
      this.basePriceEl = this.querySelector('[data-sb-base-price]');
      this.printRow = this.querySelector('[data-sb-print-row]');
      this.printLine = this.querySelector('[data-sb-print-line]');
      this.printStateEl = this.querySelector('[data-sb-print-state]');
      this.totalEl = this.querySelector('[data-sb-total]');
      this.statusEl = this.querySelector('[data-sb-status]');
    }

    _bind() {
      this.printBtn?.addEventListener('click', () => {
        const next = !this.wantPrint;
        this.wantPrint = next;
        if (!next) {
          this._setFront(null);
          this._setBack(null);
          this.printNote = '';
          if (this.printNoteEl) this.printNoteEl.value = '';
          this.printSource = 'upload';
          this.sourceInputs.forEach((el) => {
            el.checked = el.value === 'upload';
          });
          this._sync();
          return;
        }
        this._sync();
        this._openPrintUpload();
      });
      this.sourceInputs.forEach((el) => {
        el.addEventListener('change', () => {
          if (!el.checked) return;
          this.printSource = el.value === 'service' ? 'service' : 'upload';
          if (this.printSource === 'service') {
            this._setFront(null);
            this._setBack(null);
          }
          this._sync();
          if (this.printSource === 'service') {
            requestAnimationFrame(() => this.printNoteEl?.focus?.({ preventScroll: true }));
          }
        });
      });
      this.querySelectorAll('[data-sb-wa-link]').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.stopPropagation();
          const serviceInput = this.sourceInputs.find((el) => el.value === 'service');
          if (serviceInput && !serviceInput.checked) {
            serviceInput.checked = true;
            serviceInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
      this.printNoteEl?.addEventListener('input', () => {
        this.printNote = String(this.printNoteEl.value || '').trim();
      });
      this._bindFileUploads();
    }

    _placePrintAfterCtas() {
      const step = this.printStepEl || this.printBtn?.closest('.standbox-craft__step');
      if (!step) return;
      const root =
        this.closest('.product-details') ||
        document.querySelector('.product-details:has(standbox-craft)') ||
        document.querySelector('.product-information__grid .product-details');
      if (!root) return;
      const buy =
        root.querySelector('buy-buttons-component') ||
        root.querySelector('.buy-buttons') ||
        root.querySelector('.product-form-buttons');
      if (!buy) return;
      const anchor =
        buy.closest('.shopify-block') ||
        buy.closest('[data-block-type="buy-buttons"]') ||
        buy;
      if (!anchor.parentElement) return;
      if (anchor.nextElementSibling === step) {
        step.classList.add('standbox-craft__step--after-cta');
        return;
      }
      step.classList.add('standbox-craft__step--after-cta');
      anchor.insertAdjacentElement('afterend', step);
    }

    _bindFileUploads() {
      const bindInput = (input, onFile) => {
        if (!input) return;
        input.addEventListener('change', () => onFile(input.files?.[0] || null));
        const drop = input.closest('label, .standbox-craft__drop');
        if (!drop) return;
        ['dragenter', 'dragover'].forEach((ev) => {
          drop.addEventListener(ev, (e) => {
            e.preventDefault();
            e.stopPropagation();
            drop.classList.add('is-dragover');
          });
        });
        ['dragleave', 'drop'].forEach((ev) => {
          drop.addEventListener(ev, (e) => {
            e.preventDefault();
            e.stopPropagation();
            drop.classList.remove('is-dragover');
          });
        });
        drop.addEventListener('drop', (e) => {
          const file = e.dataTransfer?.files?.[0];
          if (!file) return;
          try {
            const dt = new DataTransfer();
            dt.items.add(file);
            input.files = dt.files;
          } catch (_) {}
          onFile(file);
        });
      };

      bindInput(this.fileFrontInput, (file) => this._setFront(file));
      bindInput(this.fileBackInput, (file) => this._setBack(file));
    }

    _openPrintUpload({ scroll = true } = {}) {
      this.wantPrint = true;
      if (this.uploadEl) this.uploadEl.hidden = false;
      if (this.printBtn) this.printBtn.setAttribute('aria-pressed', 'true');
      if (scroll) {
        const target =
          this.uploadEl ||
          this.printStepEl ||
          this.printBtn?.closest('.standbox-craft__step') ||
          this.printBtn;
        if (target) {
          requestAnimationFrame(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          });
        }
      }
      if (this.printSource === 'service') {
        this.printNoteEl?.focus?.({ preventScroll: true });
      } else {
        this.fileFrontInput?.focus?.({ preventScroll: true });
      }
    }

    _markHosts() {
      document.querySelectorAll(
        'product-info, .product, .product-information, .product-information__grid, product-component'
      ).forEach((el) => {
        el.classList.add('product--standboxcraft');
      });
      document.documentElement.classList.add('sb-standbox-page');
    }

    _styleCtas() {
      const buyNow = t(this.i18n, 'buyNow', 'Şimdi Al');
      const addLabel = t(this.i18n, 'addToCart', 'Sepete Ekle');
      const cellWidth = (el) => {
        const cell =
          el.closest('.accelerated-checkout-block') ||
          el.closest('span:has(add-to-cart-component)') ||
          el.closest('.product-form-buttons > *');
        const w = cell?.getBoundingClientRect?.().width || 0;
        return w > 40 ? Math.floor(w) : 0;
      };
      const stretch = (el) => {
        if (!(el instanceof HTMLElement)) return;
        const px = cellWidth(el);
        // Shopify buy-it-now custom elements ignore % width; pin to cell px.
        if (px) {
          el.style.setProperty('width', `${px}px`, 'important');
          el.style.setProperty('min-width', `${px}px`, 'important');
          el.style.setProperty('max-width', `${px}px`, 'important');
        } else {
          el.style.setProperty('width', '100%', 'important');
          el.style.setProperty('min-width', '100%', 'important');
          el.style.setProperty('max-width', '100%', 'important');
        }
        el.style.setProperty('flex', '1 1 auto', 'important');
        el.style.setProperty('align-self', 'stretch', 'important');
        el.style.setProperty('box-sizing', 'border-box', 'important');
        el.style.setProperty('display', 'flex', 'important');
      };
      const apply = () => {
        document.querySelectorAll(
          '.product-form-buttons .accelerated-checkout-block, .product-form-buttons .shopify-payment-button, .product-form-buttons shopify-buy-it-now-button, .product-form-buttons shopify-accelerated-checkout, .product-form-buttons add-to-cart-component, .product-form-buttons > span:has(add-to-cart-component)'
        ).forEach(stretch);
        document.querySelectorAll('.shopify-payment-button__button').forEach((btn) => {
          if (!(btn instanceof HTMLElement)) return;
          if (btn.textContent.trim() !== buyNow) btn.textContent = buyNow;
          btn.classList.add('sb-cta-buynow');
          stretch(btn);
          btn.style.setProperty('height', '48px', 'important');
          btn.style.setProperty('min-height', '48px', 'important');
        });
        document.querySelectorAll('.product-form-buttons .add-to-cart-button, .product-form-buttons button[name="add"]').forEach((btn) => {
          if (!(btn instanceof HTMLElement)) return;
          const labelSpan =
            btn.querySelector('.add-to-cart-text__content > span:first-child') ||
            btn.querySelector('.add-to-cart-text__content') ||
            btn.querySelector('[ref="addToCartText"]');
          if (labelSpan) {
            // Keep sibling quantity badge; only set the primary label node.
            if (labelSpan.childElementCount === 0) {
              if (labelSpan.textContent.trim() !== addLabel) labelSpan.textContent = addLabel;
            } else {
              const first = labelSpan.querySelector(':scope > span:first-child');
              if (first && first.textContent.trim() !== addLabel) first.textContent = addLabel;
            }
          }
          btn.classList.add('sb-cta-atc');
          stretch(btn);
          btn.style.setProperty('height', '48px', 'important');
          btn.style.setProperty('min-height', '48px', 'important');
        });
      };
      apply();
      requestAnimationFrame(apply);
      window.addEventListener('resize', () => {
        clearTimeout(this._ctaResizeTimer);
        this._ctaResizeTimer = setTimeout(apply, 100);
      });
      this._ctaObserver = new MutationObserver(() => {
        clearTimeout(this._ctaTimer);
        this._ctaTimer = setTimeout(apply, 50);
      });
      this._ctaObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
    }

    _galleryRoot() {
      return document.querySelector('.product-information__media media-gallery, media-gallery');
    }

    _initGalleryUi() {
      // Shared gallery/zoom/lightbox (same as other craft PDPs).
      if (window.HarfmixCraftGallery?.init) {
        const ctrl = window.HarfmixCraftGallery.init({ media: this.media || [] });
        if (ctrl) {
          this._gallery = ctrl._gallery;
          this._slideshow = ctrl._slideshow;
          this._zoomHost = ctrl._zoomHost;
        }
        return;
      }
      const gallery = this._galleryRoot();
      if (!gallery || gallery.dataset.sbGalleryUi === '1') return;
      gallery.dataset.sbGalleryUi = '1';
      this._gallery = gallery;
      gallery.classList.add('sb-gallery--minimal');
      const slideshow = gallery.querySelector('slideshow-component') || gallery;
      this._slideshow = slideshow;
      const hostTarget =
        gallery.querySelector('slideshow-container') ||
        gallery.querySelector('.product-media-container')?.parentElement ||
        gallery;
      if (hostTarget && !hostTarget.classList.contains('sb-zoom-host')) {
        hostTarget.classList.add('sb-zoom-host');
        this._zoomHost = hostTarget;
      } else {
        this._zoomHost = hostTarget;
      }
      this._hideNativeGalleryChrome(gallery);
      this._ensureCircularNav(gallery, slideshow);
      this._ensureThumbs(gallery);
      this._ensureVideoFullscreenBtn(gallery);
      this._bindHoverZoom();
      this._bindClickEnlarge();
      this._syncVideoSlideState();
    }

    _activeGallerySlide() {
      const gallery = this._gallery || this._galleryRoot();
      if (!gallery) return null;
      const slides = Array.from(gallery.querySelectorAll('slideshow-slide')).filter(
        (s) => !s.hidden && !s.classList.contains('tb-media-hidden')
      );
      return (
        slides.find((s) => s.getAttribute('aria-hidden') === 'false') ||
        slides[0] ||
        null
      );
    }

    _slideHasVideo(slide) {
      if (!slide) return false;
      return Boolean(
        slide.querySelector(
          'video, .product-media--video, deferred-media, .media-type-video, [data-media-type="video"]'
        ) ||
          slide.classList.contains('product-media-container--video') ||
          slide.querySelector('.product-media-container--video')
      );
    }

    _mediaItemAt(index) {
      if (this.media?.length) return this.media[index] || null;
      return null;
    }

    _isVideoMedia(item, index) {
      if (item?.mediaType === 'video' || item?.mediaType === 'external_video') return true;
      if (item?.videoSources?.length) return true;
      const gallery = this._gallery || this._galleryRoot();
      const slides = gallery
        ? Array.from(gallery.querySelectorAll('slideshow-slide')).filter(
            (s) => !s.hidden && !s.classList.contains('tb-media-hidden')
          )
        : [];
      return this._slideHasVideo(slides[index]);
    }

    _activeVideoEl() {
      const slide = this._activeGallerySlide();
      if (!slide) return null;
      return slide.querySelector('video');
    }

    _syncVideoSlideState() {
      const gallery = this._gallery || this._galleryRoot();
      const host = this._zoomHost || gallery;
      const isVideo = this._slideHasVideo(this._activeGallerySlide());
      host?.classList.toggle('sb-zoom-host--video', isVideo);
      gallery?.classList.toggle('sb-gallery--video', isVideo);
      if (this._videoFsBtn) this._videoFsBtn.hidden = !isVideo;
      if (this._videoToggleBtn) this._videoToggleBtn.hidden = !isVideo;
      if (this._videoMuteBtn) this._videoMuteBtn.hidden = !isVideo;
      if (isVideo) {
        host?.classList.remove('is-zooming');
        document.body.classList.remove('sb-is-zooming');
        if (this._zoomPane) this._zoomPane.style.display = 'none';
        if (this._zoomLens) this._zoomLens.style.display = 'none';
        this._hideThemeVideoPoster(this._activeGallerySlide());
      }
      this._updateGalleryVideoToggleUi();
      this._updateGalleryMuteUi();
    }

    _hideThemeVideoPoster(slide) {
      const root = slide || this._activeGallerySlide();
      if (!root) return;
      root.querySelectorAll(
        '.deferred-media__poster-button, .product-media__play-button, button[aria-label*="Play" i], button[aria-label*="Oynat" i], .media__poster-button'
      ).forEach((el) => {
        el.setAttribute('data-sb-hidden-poster', '1');
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');
      });
      root.classList.add('sb-video-controls-ready');
    }

    _updateGalleryVideoToggleUi() {
      const btn = this._videoToggleBtn;
      if (!btn || btn.hidden) return;
      const video = this._activeVideoEl();
      const playing = Boolean(video && !video.paused && !video.ended);
      btn.setAttribute('aria-label', playing ? 'Videoyu durdur' : 'Videoyu oynat');
      btn.dataset.playing = playing ? '1' : '0';
      btn.innerHTML = playing
        ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>'
        : '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
    }

    _updateGalleryMuteUi() {
      const btn = this._videoMuteBtn;
      if (!btn || btn.hidden) return;
      const video = this._activeVideoEl();
      const muted = video ? Boolean(video.muted) : Boolean(this._videoMuted);
      btn.setAttribute('aria-label', muted ? 'Sesi aç' : 'Sessize al');
      btn.dataset.muted = muted ? '1' : '0';
      btn.innerHTML = muted
        ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><path d="M11 5L6 9H3v6h3l5 4V5z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M16 9l5 5M21 9l-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>'
        : '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><path d="M11 5L6 9H3v6h3l5 4V5z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M15.5 8.5a5 5 0 010 7M18 6a8 8 0 010 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
    }

    _ensureVideoFullscreenBtn(gallery) {
      const host = this._zoomHost || gallery;
      if (!host) return;
      if (typeof this._videoMuted !== 'boolean') this._videoMuted = false;

      let toggle = host.querySelector('[data-sb-video-toggle]');
      if (!toggle) {
        toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'sb-video-toggle';
        toggle.setAttribute('data-sb-video-toggle', '');
        toggle.setAttribute('aria-label', 'Videoyu oynat');
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._toggleActiveVideo();
        });
        host.appendChild(toggle);
      }
      this._videoToggleBtn = toggle;

      let mute = host.querySelector('[data-sb-video-mute]');
      if (!mute) {
        mute = document.createElement('button');
        mute.type = 'button';
        mute.className = 'sb-video-mute';
        mute.setAttribute('data-sb-video-mute', '');
        mute.setAttribute('aria-label', 'Sessize al');
        mute.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._toggleActiveMute();
        });
        host.appendChild(mute);
      }
      this._videoMuteBtn = mute;

      let btn = host.querySelector('[data-sb-video-fs]');
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sb-video-fs';
        btn.setAttribute('data-sb-video-fs', '');
        btn.setAttribute('aria-label', 'Videoyu tam ekran oynat');
        btn.innerHTML =
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 9h6v6H9z" stroke="currentColor" stroke-width="1.8"/></svg>';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._playVideoFullscreen();
        });
        host.appendChild(btn);
      }
      this._videoFsBtn = btn;

      // Hide theme center play overlays so only our controls remain
      gallery.querySelectorAll('slideshow-slide').forEach((slide) => {
        if (this._slideHasVideo(slide)) this._hideThemeVideoPoster(slide);
      });

      if (gallery.dataset.sbVideoFsObs !== '1') {
        gallery.dataset.sbVideoFsObs = '1';
        let timer = null;
        const obs = new MutationObserver(() => {
          if (this._videoBusy) return;
          clearTimeout(timer);
          timer = setTimeout(() => this._syncVideoSlideState(), 40);
        });
        obs.observe(gallery, {
          attributes: true,
          subtree: true,
          attributeFilter: ['aria-hidden', 'class'],
        });
        this._videoFsObserver = obs;
      }
    }

    _activeMediaIndex() {
      const gallery = this._gallery || this._galleryRoot();
      const slides = gallery
        ? Array.from(gallery.querySelectorAll('slideshow-slide')).filter(
            (s) => !s.hidden && !s.classList.contains('tb-media-hidden')
          )
        : [];
      const slide = this._activeGallerySlide();
      const idx = slide ? slides.indexOf(slide) : -1;
      return idx >= 0 ? idx : 0;
    }

    _injectVideoFromConfig(slide, index) {
      const item = this._mediaItemAt(typeof index === 'number' ? index : this._activeMediaIndex());
      const sources = item?.videoSources?.filter((s) => s?.url) || [];
      if (!sources.length || !slide) return null;
      let video = slide.querySelector('video[data-sb-injected]');
      if (!video) {
        video = document.createElement('video');
        video.setAttribute('data-sb-injected', '1');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.controls = true;
        video.preload = 'metadata';
        if (item.preview || item.src) video.poster = item.preview || item.src;
        sources.forEach((s) => {
          const el = document.createElement('source');
          el.src = s.url;
          if (s.type) el.type = s.type;
          video.appendChild(el);
        });
        const host =
          slide.querySelector('.product-media, .product-media-container, deferred-media, .media') || slide;
        // Prefer replacing poster area
        const poster = host.querySelector('img');
        if (poster?.parentElement) poster.parentElement.appendChild(video);
        else host.appendChild(video);
      }
      slide.classList.add('sb-video-playing-host');
      return video;
    }

    _activateDeferredMedia(slide) {
      const root = slide || this._activeGallerySlide();
      if (!root) return null;
      const existing = root.querySelector('video');
      if (existing) {
        this._hideThemeVideoPoster(root);
        return existing;
      }
      const deferred = root.querySelector('deferred-media');
      if (deferred) {
        try {
          if (typeof deferred.loadContent === 'function') deferred.loadContent(false);
          else if (!deferred.hasAttribute('loaded')) {
            const tpl = deferred.querySelector('template');
            if (tpl?.content) {
              deferred.appendChild(tpl.content.cloneNode(true));
              deferred.setAttribute('loaded', '');
            }
          }
        } catch (_) {}
      }
      let video = root.querySelector('video');
      if (!video) {
        const gallery = this._gallery || this._galleryRoot();
        const slides = gallery
          ? Array.from(gallery.querySelectorAll('slideshow-slide')).filter(
              (s) => !s.hidden && !s.classList.contains('tb-media-hidden')
            )
          : [];
        video = this._injectVideoFromConfig(root, slides.indexOf(root));
      }
      if (video) this._hideThemeVideoPoster(root);
      return video;
    }

    async _waitForVideo(slide, tries = 12) {
      let video = this._activateDeferredMedia(slide);
      for (let i = 0; !video && i < tries; i += 1) {
        await new Promise((r) => setTimeout(r, 40));
        video = this._activateDeferredMedia(slide);
      }
      return video;
    }

    async _playMediaElement(video, { mutedFallback = true } = {}) {
      if (!video) return false;
      video.controls = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      const preferMuted = Boolean(this._videoMuted);
      try {
        video.muted = preferMuted;
        const p = video.play?.();
        if (p?.then) await p;
        this._updateGalleryMuteUi();
        return true;
      } catch (_) {
        if (!mutedFallback || preferMuted) return false;
        try {
          video.muted = true;
          this._videoMuted = true;
          await video.play?.();
          this._updateGalleryMuteUi();
          return true;
        } catch (_) {
          return false;
        }
      }
    }

    _toggleActiveMute() {
      const video = this._activeVideoEl();
      this._videoMuted = !(video ? video.muted : this._videoMuted);
      if (video) video.muted = this._videoMuted;
      if (this._lightboxVideo && !this._lightboxVideo.hidden) {
        this._lightboxVideo.muted = this._videoMuted;
      }
      this._updateGalleryMuteUi();
      this._syncLightboxMuteUi();
    }

    async _toggleActiveVideo() {
      if (this._videoBusy) return;
      this._videoBusy = true;
      try {
        let video = this._activeVideoEl() || (await this._waitForVideo());
        if (!video) return;
        if (!video.paused && !video.ended) {
          video.pause();
        } else {
          await this._playMediaElement(video);
        }
        this._updateGalleryVideoToggleUi();
        video.onplay = () => this._updateGalleryVideoToggleUi();
        video.onpause = () => this._updateGalleryVideoToggleUi();
        video.onended = () => this._updateGalleryVideoToggleUi();
      } finally {
        this._videoBusy = false;
      }
    }

    async _playActiveVideo({ fullscreen = false } = {}) {
      if (this._videoBusy) return;
      this._videoBusy = true;
      try {
        const video = this._activeVideoEl() || (await this._waitForVideo());
        if (!video) return;
        await this._playMediaElement(video);
        this._updateGalleryVideoToggleUi();
        if (!fullscreen) return;
        try {
          if (video.requestFullscreen) await video.requestFullscreen();
          else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
          else if (video.webkitRequestFullscreen) await video.webkitRequestFullscreen();
          else if (video.msRequestFullscreen) await video.msRequestFullscreen();
        } catch (_) {}
      } finally {
        this._videoBusy = false;
      }
    }

    async _playVideoFullscreen() {
      await this._playActiveVideo({ fullscreen: true });
    }

    _pauseAllGalleryVideos() {
      const gallery = this._gallery || this._galleryRoot();
      gallery?.querySelectorAll('video').forEach((v) => {
        try {
          v.pause();
        } catch (_) {}
      });
      this._updateGalleryVideoToggleUi();
    }

    _hideNativeGalleryChrome(gallery) {
      gallery.querySelectorAll('slideshow-arrows').forEach((el) => {
        el.setAttribute('hidden', '');
        el.style.setProperty('display', 'none', 'important');
        el.setAttribute('data-sb-hidden-nav', '1');
      });
      gallery.querySelectorAll('slideshow-controls').forEach((el) => {
        el.setAttribute('hidden', '');
        el.style.setProperty('display', 'none', 'important');
        el.setAttribute('data-sb-hidden-thumbs', '1');
      });
    }

    _teardownGalleryUi() {
      this._ctaObserver?.disconnect();
      this._videoFsObserver?.disconnect();
      this._videoFsObserver = null;
      this._videoFsBtn?.remove();
      this._videoFsBtn = null;
      this._videoToggleBtn?.remove();
      this._videoToggleBtn = null;
      this._videoMuteBtn?.remove();
      this._videoMuteBtn = null;
      if (this._zoomHost) {
        this._zoomHost.removeEventListener('pointermove', this._onZoomMove);
        this._zoomHost.removeEventListener('pointerleave', this._onZoomLeave);
        this._zoomHost.removeEventListener('pointerenter', this._onZoomEnter);
        this._zoomHost.classList.remove('is-zooming', 'sb-zoom-host--video');
      }
      if (this._onZoomScroll) {
        document.querySelector('.page-wrapper')?.removeEventListener('scroll', this._onZoomScroll);
        window.removeEventListener('scroll', this._onZoomScroll);
        document.removeEventListener('scroll', this._onZoomScroll, true);
        this._onZoomScroll = null;
      }
      document.body.classList.remove('sb-is-zooming');
      this._zoomPane?.remove();
      this._zoomLens?.remove();
      this._zoomPane = null;
      this._zoomLens = null;
      this._closeLightbox();
      this._lightbox?.remove();
      this._lightbox = null;
      if (this._onLightboxKey) {
        document.removeEventListener('keydown', this._onLightboxKey);
        this._onLightboxKey = null;
      }
      if (this._gallery && this._onEnlargeClick) {
        this._gallery.removeEventListener('click', this._onEnlargeClick, true);
      }
    }

    _ensureCircularNav(gallery, slideshow) {
      if (gallery.querySelector('.sb-gallery-nav')) return;
      const host = this._zoomHost || gallery;
      const mk = (dir) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `sb-gallery-nav sb-gallery-nav--${dir}`;
        btn.setAttribute('aria-label', dir === 'prev' ? 'Önceki görsel' : 'Sonraki görsel');
        btn.innerHTML =
          dir === 'prev'
            ? '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10.5 3.5L6 8l4.5 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M5.5 3.5L10 8l-4.5 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._stepGallery(dir === 'next' ? 1 : -1);
        });
        return btn;
      };
      host.appendChild(mk('prev'));
      host.appendChild(mk('next'));
    }

    _stepGallery(delta) {
      const gallery = this._gallery || this._galleryRoot();
      const slides = gallery
        ? Array.from(gallery.querySelectorAll('slideshow-slide')).filter((s) => !s.hidden && !s.classList.contains('tb-media-hidden'))
        : [];
      if (!slides.length) return;
      let current = slides.findIndex((s) => s.getAttribute('aria-hidden') === 'false');
      if (current < 0) current = 0;
      const next = (current + delta + slides.length) % slides.length;
      this._selectGallerySlide(next);
    }

    /** Select main gallery slide + thumbs + zoom (used by nav and lightbox sync). */
    _selectGallerySlide(index, { animate = true } = {}) {
      const gallery = this._gallery || this._galleryRoot();
      if (!gallery) return;
      const slideshow = gallery.querySelector('slideshow-component') || gallery.slideshow || this._slideshow;
      const slides = Array.from(gallery.querySelectorAll('slideshow-slide')).filter(
        (s) => !s.hidden && !s.classList.contains('tb-media-hidden')
      );
      if (!slides.length) return;
      const i = ((index % slides.length) + slides.length) % slides.length;
      this._pauseAllGalleryVideos();
      if (slideshow && typeof slideshow.select === 'function') {
        slideshow.select(i);
      } else {
        slides[i]?.click?.();
        const thumb = gallery.querySelectorAll('.sb-thumbs button')[i];
        thumb?.click?.();
      }
      this._setActiveThumb(i);
      this._syncZoomImage(i);
      this._syncVideoSlideState();
      if (animate) {
        const host = this._zoomHost || gallery;
        host.classList.remove('sb-gallery-sync');
        void host.offsetWidth;
        host.classList.add('sb-gallery-sync');
        clearTimeout(this._gallerySyncTimer);
        this._gallerySyncTimer = setTimeout(() => host.classList.remove('sb-gallery-sync'), 360);
      }
    }

    _ensureThumbs(gallery) {
      if (gallery.querySelector('.sb-thumbs')) return;
      // Prefer one entry per slideshow-slide — nested .product-media img must not double-count.
      const media = this.media.length
        ? this.media
        : (() => {
            const slides = Array.from(gallery.querySelectorAll('slideshow-slide')).filter(
              (s) => !s.hidden && !s.classList.contains('tb-media-hidden')
            );
            if (slides.length) {
              return slides.map((slide, i) => {
                const img = slide.querySelector('img');
                return {
                  id: i,
                  preview: img?.currentSrc || img?.src || '',
                  src: img?.currentSrc || img?.src || '',
                  zoom: img?.currentSrc || img?.src || '',
                  alt: img?.alt || '',
                };
              });
            }
            return Array.from(gallery.querySelectorAll('.product-media img')).map((img, i) => ({
              id: i,
              preview: img.currentSrc || img.src,
              src: img.currentSrc || img.src,
              zoom: img.currentSrc || img.src,
              alt: img.alt || '',
            }));
          })();
      if (media.length < 2) return;
      const row = document.createElement('div');
      row.className = 'sb-thumbs';
      row.setAttribute('role', 'tablist');
      media.forEach((m, index) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('role', 'tab');
        if (index === 0) btn.setAttribute('aria-current', 'true');
        const img = document.createElement('img');
        img.src = m.preview || m.src;
        img.alt = m.alt || `Görsel ${index + 1}`;
        img.loading = 'lazy';
        btn.appendChild(img);
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._selectGallerySlide(index);
        });
        row.appendChild(btn);
      });
      gallery.appendChild(row);
    }

    _setActiveThumb(index) {
      const gallery = this._gallery || this._galleryRoot();
      gallery?.querySelectorAll('.sb-thumbs button').forEach((btn, i) => {
        if (i === index) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
    }

    _currentZoomUrl(index) {
      if (this.media[index]?.zoom || this.media[index]?.src) {
        return this.media[index].zoom || this.media[index].src;
      }
      const gallery = this._gallery || this._galleryRoot();
      const img = gallery?.querySelectorAll('slideshow-slide img, .product-media img')[index];
      return img?.currentSrc || img?.src || '';
    }

    _syncZoomImage(index) {
      this._zoomIndex = index;
      const url = this._currentZoomUrl(index);
      if (this._zoomPane && url) {
        const bg = `url("${url}")`;
        this._zoomPane.dataset.bg = bg;
        this._zoomPane.style.backgroundImage = bg;
      }
    }

    _bindClickEnlarge() {
      const gallery = this._gallery || this._galleryRoot();
      if (!gallery || gallery.dataset.sbLightboxBound === '1') return;
      gallery.dataset.sbLightboxBound = '1';

      const patchZoom = () => {
        document.querySelectorAll('zoom-dialog').forEach((zd) => {
          if (zd.dataset.sbPatched === '1') return;
          zd.dataset.sbPatched = '1';
          zd.open = (index, event) => {
            try {
              event?.preventDefault?.();
              event?.stopPropagation?.();
            } catch (_) {}
            this._openLightbox(typeof index === 'number' ? index : 0);
          };
        });
        document.querySelectorAll('media-gallery').forEach((mg) => {
          if (mg.dataset.sbZoomPatched === '1') return;
          mg.dataset.sbZoomPatched = '1';
          if (typeof mg.zoom === 'function') {
            mg.zoom = (index, event) => {
              try {
                event?.preventDefault?.();
                event?.stopPropagation?.();
              } catch (_) {}
              const slides = Array.from(gallery.querySelectorAll('slideshow-slide')).filter(
                (s) => !s.hidden && !s.classList.contains('tb-media-hidden')
              );
              const i = typeof index === 'number' ? index : 0;
              const slide = slides[i] || this._activeGallerySlide();
              if (this._slideHasVideo(slide) || this._isVideoMedia(this._mediaItemAt(i), i)) {
                this._toggleActiveVideo();
                return;
              }
              this._openLightbox(i);
            };
          }
        });
      };
      patchZoom();
      setTimeout(patchZoom, 200);
      setTimeout(patchZoom, 800);

      this._onEnlargeClick = (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;
        if (
          t.closest(
            '.sb-gallery-nav, .sb-thumbs, slideshow-arrows, slideshow-controls, .sb-video-fs, .sb-video-toggle, .sb-video-mute, .sb-lightbox'
          )
        )
          return;
        if (this._videoBusy) return;
        const slide = t.closest('slideshow-slide') || this._activeGallerySlide();
        if (this._slideHasVideo(slide) || t.closest('video, deferred-media, .deferred-media__poster-button')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation?.();
          this._toggleActiveVideo();
          return;
        }
        const media = t.closest('slideshow-slide, .product-media-container, .product-media, img.product-media__image');
        if (!media) return;
        e.preventDefault();
        e.stopPropagation();
        const slides = Array.from(gallery.querySelectorAll('slideshow-slide')).filter(
          (s) => !s.hidden && !s.classList.contains('tb-media-hidden')
        );
        let idx = 0;
        if (slide && slides.length) idx = Math.max(0, slides.indexOf(slide));
        else if (typeof this._zoomIndex === 'number') idx = this._zoomIndex;
        this._openLightbox(idx);
      };
      gallery.addEventListener('click', this._onEnlargeClick, true);
    }

    _ensureLightbox() {
      if (this._lightbox) return this._lightbox;
      const root = document.createElement('div');
      root.className = 'sb-lightbox';
      root.setAttribute('hidden', '');
      root.innerHTML = `
        <button type="button" class="sb-lightbox__backdrop" aria-label="Kapat"></button>
        <div class="sb-lightbox__panel" role="dialog" aria-modal="true" aria-label="Ürün görseli">
          <button type="button" class="sb-lightbox__close" aria-label="Kapat">×</button>
          <button type="button" class="sb-lightbox__nav sb-lightbox__nav--prev" aria-label="Önceki görsel">
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10.5 3.5L6 8l4.5 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div class="sb-lightbox__stage">
            <img class="sb-lightbox__img" alt="" />
            <video class="sb-lightbox__video" playsinline webkit-playsinline controls hidden></video>
            <div class="sb-lightbox__video-actions" hidden>
              <button type="button" class="sb-lightbox__vbtn" data-sb-lb-toggle aria-label="Durdur">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
              </button>
              <button type="button" class="sb-lightbox__vbtn" data-sb-lb-mute aria-label="Sessize al">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><path d="M11 5L6 9H3v6h3l5 4V5z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M15.5 8.5a5 5 0 010 7M18 6a8 8 0 010 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
              </button>
              <button type="button" class="sb-lightbox__vbtn" data-sb-lb-fs aria-label="Tam ekran">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 9h6v6H9z" stroke="currentColor" stroke-width="1.8"/></svg>
              </button>
            </div>
          </div>
          <button type="button" class="sb-lightbox__nav sb-lightbox__nav--next" aria-label="Sonraki görsel">
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M5.5 3.5L10 8l-4.5 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>`;
      document.body.appendChild(root);
      this._lightbox = root;
      this._lightboxImg = root.querySelector('.sb-lightbox__img');
      this._lightboxVideo = root.querySelector('.sb-lightbox__video');
      this._lightboxVideoActions = root.querySelector('.sb-lightbox__video-actions');
      root.querySelector('.sb-lightbox__backdrop')?.addEventListener('click', () => this._closeLightbox());
      root.querySelector('.sb-lightbox__close')?.addEventListener('click', () => this._closeLightbox());
      root.querySelector('.sb-lightbox__nav--prev')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._stepLightbox(-1);
      });
      root.querySelector('.sb-lightbox__nav--next')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._stepLightbox(1);
      });
      root.querySelector('[data-sb-lb-toggle]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggleLightboxVideo();
      });
      root.querySelector('[data-sb-lb-mute]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggleActiveMute();
      });
      root.querySelector('[data-sb-lb-fs]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._fullscreenLightboxVideo();
      });
      this._onLightboxKey = (e) => {
        if (!root.classList.contains('is-open')) return;
        if (e.key === 'Escape') this._closeLightbox();
        if (e.key === 'ArrowLeft') this._stepLightbox(-1);
        if (e.key === 'ArrowRight') this._stepLightbox(1);
        if (e.key === ' ' || e.key === 'k') {
          e.preventDefault();
          this._toggleLightboxVideo();
        }
      };
      document.addEventListener('keydown', this._onLightboxKey);
      return root;
    }

    _lightboxSources() {
      if (this.media?.length) {
        return this.media.map((m, index) => ({
          index,
          alt: m.alt || 'Ürün görseli',
          image: m.zoom || m.src || m.preview || '',
          mediaType: m.mediaType || 'image',
          videoSources: m.videoSources || [],
          externalHost: m.externalHost || '',
          externalId: m.externalId || '',
        }));
      }
      const gallery = this._gallery || this._galleryRoot();
      const slides = Array.from(gallery?.querySelectorAll('slideshow-slide') || []).filter(
        (s) => !s.hidden && !s.classList.contains('tb-media-hidden')
      );
      return slides.map((slide, index) => {
        const img = slide.querySelector('img');
        const video = slide.querySelector('video');
        const sources = video
          ? Array.from(video.querySelectorAll('source')).map((s) => ({ url: s.src, type: s.type }))
          : video?.currentSrc
            ? [{ url: video.currentSrc, type: '' }]
            : [];
        return {
          index,
          alt: img?.alt || 'Ürün görseli',
          image: img?.currentSrc || img?.src || '',
          mediaType: this._slideHasVideo(slide) ? 'video' : 'image',
          videoSources: sources,
        };
      });
    }

    _stopLightboxVideo() {
      const video = this._lightboxVideo;
      if (!video) return;
      try {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((s) => s.remove());
        video.load();
      } catch (_) {}
      video.hidden = true;
      if (this._lightboxVideoActions) this._lightboxVideoActions.hidden = true;
      if (this._lightboxImg) this._lightboxImg.hidden = false;
      this._syncLightboxToggleUi(false);
    }

    _syncLightboxToggleUi(playing) {
      const btn = this._lightbox?.querySelector('[data-sb-lb-toggle]');
      if (!btn) return;
      btn.setAttribute('aria-label', playing ? 'Durdur' : 'Oynat');
      btn.innerHTML = playing
        ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>'
        : '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
      this._syncLightboxMuteUi();
    }

    _syncLightboxMuteUi() {
      const btn = this._lightbox?.querySelector('[data-sb-lb-mute]');
      if (!btn) return;
      const video = this._lightboxVideo;
      const muted = video && !video.hidden ? Boolean(video.muted) : Boolean(this._videoMuted);
      btn.setAttribute('aria-label', muted ? 'Sesi aç' : 'Sessize al');
      btn.innerHTML = muted
        ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><path d="M11 5L6 9H3v6h3l5 4V5z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M16 9l5 5M21 9l-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>'
        : '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><path d="M11 5L6 9H3v6h3l5 4V5z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M15.5 8.5a5 5 0 010 7M18 6a8 8 0 010 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
    }

    async _toggleLightboxVideo() {
      const video = this._lightboxVideo;
      if (!video || video.hidden) return;
      if (!video.paused && !video.ended) {
        video.pause();
        this._syncLightboxToggleUi(false);
        return;
      }
      const ok = await this._playMediaElement(video);
      this._syncLightboxToggleUi(ok);
    }

    async _fullscreenLightboxVideo() {
      const video = this._lightboxVideo;
      if (!video || video.hidden) return;
      await this._playMediaElement(video);
      this._syncLightboxToggleUi(true);
      try {
        if (video.requestFullscreen) await video.requestFullscreen();
        else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
        else if (video.webkitRequestFullscreen) await video.webkitRequestFullscreen();
        else if (video.msRequestFullscreen) await video.msRequestFullscreen();
      } catch (_) {}
    }

    async _showLightboxMedia(item) {
      const img = this._lightboxImg;
      const video = this._lightboxVideo;
      const actions = this._lightboxVideoActions;
      if (!img || !video) return;

      this._stopLightboxVideo();

      const isVideo = item.mediaType === 'video' || (item.videoSources && item.videoSources.length);
      if (!isVideo) {
        img.hidden = false;
        img.src = item.image || '';
        img.alt = item.alt || 'Ürün görseli';
        video.hidden = true;
        if (actions) actions.hidden = true;
        return;
      }

      img.hidden = true;
      video.hidden = false;
      if (actions) actions.hidden = false;
      video.poster = item.image || '';
      video.controls = true;
      video.loop = false;
      video.muted = Boolean(this._videoMuted);
      video.querySelectorAll('source').forEach((s) => s.remove());

      let sources = item.videoSources || [];
      if (!sources.length) {
        // Fall back to activating the on-page deferred video and cloning its sources
        const gallery = this._gallery || this._galleryRoot();
        const slides = Array.from(gallery?.querySelectorAll('slideshow-slide') || []).filter(
          (s) => !s.hidden && !s.classList.contains('tb-media-hidden')
        );
        const slide = slides[item.index];
        const pageVideo = await this._waitForVideo(slide);
        if (pageVideo) {
          sources = Array.from(pageVideo.querySelectorAll('source')).map((s) => ({
            url: s.src,
            type: s.type,
          }));
          if (!sources.length && pageVideo.currentSrc) sources = [{ url: pageVideo.currentSrc, type: '' }];
        }
      }

      if (sources.length) {
        sources.forEach((s) => {
          if (!s?.url) return;
          const el = document.createElement('source');
          el.src = s.url;
          if (s.type) el.type = s.type;
          video.appendChild(el);
        });
        video.load();
      } else if (item.image) {
        // Still show poster image if video URL missing
        img.hidden = false;
        img.src = item.image;
        video.hidden = true;
        if (actions) actions.hidden = true;
        return;
      }

      video.onplay = () => this._syncLightboxToggleUi(true);
      video.onpause = () => this._syncLightboxToggleUi(false);
      video.onended = () => this._syncLightboxToggleUi(false);
      const ok = await this._playMediaElement(video);
      this._syncLightboxToggleUi(ok);
    }

    _openLightbox(index = 0) {
      const sources = this._lightboxSources();
      if (!sources.length) return;
      const root = this._ensureLightbox();
      const i = ((index % sources.length) + sources.length) % sources.length;
      this._lightboxIndex = i;
      const item = sources[i];
      const multi = sources.length > 1;
      root.querySelectorAll('.sb-lightbox__nav').forEach((btn) => {
        btn.hidden = !multi;
      });
      root.removeAttribute('hidden');
      root.classList.add('is-open');
      this._setPageScrollLock(true);
      this._zoomHost?.classList.remove('is-zooming');
      document.body.classList.remove('sb-is-zooming');
      // Fire and forget — autoplay video when landing on a video slide
      this._showLightboxMedia(item);
    }

    _setPageScrollLock(locked) {
      const wrap = document.querySelector('.page-wrapper');
      if (locked) {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        if (wrap) {
          this._pageWrapOverflow = wrap.style.overflow;
          wrap.style.overflow = 'hidden';
        }
      } else {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        if (wrap) wrap.style.overflow = this._pageWrapOverflow || '';
        this._pageWrapOverflow = null;
      }
    }

    _stepLightbox(delta) {
      const sources = this._lightboxSources();
      if (!sources.length) return;
      const next = (((this._lightboxIndex || 0) + delta) % sources.length + sources.length) % sources.length;
      this._selectGallerySlide(next, { animate: true });
      this._openLightbox(next);
    }

    _closeLightbox() {
      const root = this._lightbox;
      if (!root) return;
      this._stopLightboxVideo();
      root.classList.remove('is-open');
      root.setAttribute('hidden', '');
      this._setPageScrollLock(false);
    }

    _bindHoverZoom() {
      const host = this._zoomHost;
      if (!host || host.dataset.sbZoomBound === '1') return;
      host.dataset.sbZoomBound = '1';

      // Lens lives on the image host; pane must NOT — slideshow-container is overflow:hidden
      // and would clip a right-side magnified panel (root cause of broken hover zoom).
      const lens = document.createElement('div');
      lens.className = 'sb-zoom-lens';
      host.appendChild(lens);

      const pane = document.createElement('div');
      pane.className = 'sb-zoom-pane';
      pane.setAttribute('aria-hidden', 'true');
      document.body.appendChild(pane);

      this._zoomLens = lens;
      this._zoomPane = pane;
      this._syncZoomImage(0);

      const isDesktop = () => window.matchMedia('(min-width: 990px) and (hover: hover)').matches;
      // Lock pane viewport box on first show so page scroll does not drag it along.
      let paneBox = null;

      const applyPaneBox = () => {
        if (!paneBox) return;
        pane.style.setProperty('position', 'fixed', 'important');
        pane.style.setProperty('left', `${paneBox.left}px`, 'important');
        pane.style.setProperty('top', `${paneBox.top}px`, 'important');
        pane.style.setProperty('right', 'auto', 'important');
        pane.style.setProperty('bottom', 'auto', 'important');
        pane.style.setProperty('width', `${paneBox.width}px`, 'important');
        pane.style.setProperty('height', `${paneBox.height}px`, 'important');
        pane.style.setProperty('z-index', '10040', 'important');
        pane.style.pointerEvents = 'none';
        pane.style.display = 'block';
        pane.style.backgroundColor = '#fff';
        pane.style.backgroundRepeat = 'no-repeat';
        pane.style.border = '1px solid rgba(0,0,0,.1)';
        pane.style.boxShadow = '0 12px 32px rgba(0,0,0,.14)';
        pane.style.borderRadius = '4px';
        if (pane.dataset.bg) pane.style.backgroundImage = pane.dataset.bg;
      };

      const positionPane = ({ force = false } = {}) => {
        if (paneBox && !force) {
          applyPaneBox();
          return;
        }
        const rect = host.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const gap = 14;
        const details =
          document.querySelector('.product-details') ||
          document.querySelector('.product-information__details') ||
          document.querySelector('.product-information .product-details');
        const detailsRect = details?.getBoundingClientRect?.();
        let left;
        let width;
        if (detailsRect && detailsRect.width > 180 && detailsRect.left > rect.left + 40) {
          left = Math.max(rect.right + gap, detailsRect.left);
          width = Math.min(440, Math.max(260, detailsRect.width));
          if (left + width > window.innerWidth - 12) {
            width = Math.max(220, window.innerWidth - left - 12);
          }
        } else {
          const maxW = Math.min(440, Math.max(260, window.innerWidth - rect.right - gap - 20));
          left = rect.right + gap;
          width = maxW;
          if (left + 280 > window.innerWidth - 12) {
            width = Math.min(440, Math.max(260, window.innerWidth * 0.34));
            left = Math.min(rect.right + gap, window.innerWidth - width - 12);
            left = Math.max(12, left);
          }
        }
        const top = Math.max(12, Math.min(rect.top, window.innerHeight - 200));
        const height = Math.min(
          Math.max(rect.height, 280),
          window.innerHeight - top - 16,
          560
        );
        paneBox = { left, top, width, height };
        applyPaneBox();
      };

      const hideZoom = () => {
        host.classList.remove('is-zooming');
        document.body.classList.remove('sb-is-zooming');
        pane.style.display = 'none';
        lens.style.display = 'none';
        paneBox = null;
      };

      this._onZoomEnter = (e) => {
        if (!isDesktop()) return;
        if (this._slideHasVideo(this._activeGallerySlide())) return;
        // Don't start zoom when hovering nav / video controls
        if (e?.target?.closest?.('.sb-gallery-nav, slideshow-arrows, .sb-video-fs, .sb-video-toggle, .sb-video-mute, video, deferred-media')) return;
        host.classList.add('is-zooming');
        document.body.classList.add('sb-is-zooming');
        lens.style.display = 'block';
        positionPane({ force: true });
      };
      this._onZoomLeave = () => {
        hideZoom();
      };
      this._onZoomMove = (e) => {
        if (!isDesktop()) return;
        if (this._slideHasVideo(this._activeGallerySlide())) {
          hideZoom();
          return;
        }
        if (e?.target?.closest?.('.sb-gallery-nav, slideshow-arrows, .sb-video-fs, .sb-video-toggle, .sb-video-mute, video, deferred-media')) {
          hideZoom();
          return;
        }
        if (!host.classList.contains('is-zooming')) {
          host.classList.add('is-zooming');
          document.body.classList.add('sb-is-zooming');
          lens.style.display = 'block';
          positionPane({ force: true });
        } else if (pane.style.display !== 'block') {
          applyPaneBox();
        }
        const rect = host.getBoundingClientRect();
        const lensW = lens.offsetWidth || 140;
        const lensH = lens.offsetHeight || 180;
        let x = e.clientX - rect.left - lensW / 2;
        let y = e.clientY - rect.top - lensH / 2;
        x = Math.max(0, Math.min(x, Math.max(0, rect.width - lensW)));
        y = Math.max(0, Math.min(y, Math.max(0, rect.height - lensH)));
        lens.style.left = `${x}px`;
        lens.style.top = `${y}px`;
        lens.style.display = 'block';

        const denomX = Math.max(1, rect.width - lensW);
        const denomY = Math.max(1, rect.height - lensH);
        const bgX = (x / denomX) * 100;
        const bgY = (y / denomY) * 100;
        const zoomScale = Math.max(200, (rect.width / Math.max(1, lensW)) * 100);
        pane.style.backgroundPosition = `${bgX}% ${bgY}%`;
        pane.style.backgroundSize = `${zoomScale}%`;
      };

      this._onZoomScroll = () => {
        if (!host.classList.contains('is-zooming') || !paneBox) return;
        applyPaneBox();
      };

      host.addEventListener('pointerenter', this._onZoomEnter);
      host.addEventListener('pointerleave', this._onZoomLeave);
      host.addEventListener('pointermove', this._onZoomMove);
      document.querySelector('.page-wrapper')?.addEventListener('scroll', this._onZoomScroll, { passive: true });
      window.addEventListener('scroll', this._onZoomScroll, { passive: true });
      document.addEventListener('scroll', this._onZoomScroll, { passive: true, capture: true });
    }

    _interceptCart() {
      const isOurAtcButton = (el) => {
        if (!(el instanceof Element)) return false;
        const btn = el.closest('button[type="submit"], button[name="add"], [name="add"]');
        if (!btn || btn.closest('standbox-craft')) return false;
        return (
          document.body.contains(this) &&
          !!btn.closest('sticky-add-to-cart, .sticky-add-to-cart, product-form, .product-form, form[action*="/cart/add"], product-form-component')
        );
      };
      const guard = (event) => {
        if (event.type === 'submit') {
          const form = event.target instanceof HTMLFormElement ? event.target : event.target.closest?.('form');
          if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.action || '')) return;
          if (form.closest('standbox-craft') || !document.body.contains(this)) return;
          event.preventDefault();
          event.stopPropagation();
          this._addToCart();
          return;
        }
        if (!isOurAtcButton(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        this._addToCart();
      };
      document.addEventListener('click', guard, true);
      document.addEventListener('submit', guard, true);
    }

    _findVariant(contains, excludes) {
      const list = Array.isArray(contains) ? contains : [];
      const excl = excludes || [];
      // Empty contains = first variant that is not excluded (e.g. no-print products).
      if (!list.length) {
        return (
          this.variants.find((v) => excludesAny(v.title, excl)) ||
          this.variants[0] ||
          null
        );
      }
      return (
        this.variants.find(
          (v) => includesAny(v.title, list) && excludesAny(v.title, excl)
        ) || null
      );
    }


    _resolved() {
      const opt = this.option;
      const base = this._findVariant(opt.baseContains, opt.baseExcludes);
      const print = this.printEnabled
        ? this._findVariant(opt.printContains || [], [])
        : null;
      return { opt, base, print };
    }

    _setFront(file) {
      this.fileFront = file || null;
      if (this._frontUrl) URL.revokeObjectURL(this._frontUrl);
      this._frontUrl = file && file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
      if (this.fileFrontInput && !file) this.fileFrontInput.value = '';
      this._sync();
    }

    _setBack(file) {
      this.fileBack = file || null;
      if (this._backUrl) URL.revokeObjectURL(this._backUrl);
      this._backUrl = file && file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
      if (this.fileBackInput && !file) this.fileBackInput.value = '';
      this._sync();
    }

    _setStatus(msg, tone) {
      if (!this.statusEl) return;
      if (!msg) {
        this.statusEl.hidden = true;
        this.statusEl.textContent = '';
        return;
      }
      this.statusEl.hidden = false;
      this.statusEl.textContent = msg;
      this.statusEl.dataset.tone = tone || 'success';
    }

    _sync() {
      const i18n = this.i18n;
      if (!this.printEnabled) this.wantPrint = false;
      const { opt, base, print } = this._resolved();

      if (this.descEl && typeof opt?.description === 'string' && opt.description) {
        this.descEl.innerHTML = opt.description;
      }

      if (this.printBtn) this.printBtn.setAttribute('aria-pressed', this.wantPrint ? 'true' : 'false');
      if (this.printMeta) {
        this.printMeta.textContent = this.wantPrint
          ? this._printBillingMeta(i18n)
          : `${t(i18n, 'printSizeLabel', 'Baskı alanı')}: ${opt?.printArea || ''}`;
      }
      if (this.printPriceEl) {
        this.printPriceEl.textContent = this.wantPrint && print ? `+ ${moneyOf(print)}` : '—';
      }
      if (this.uploadEl) this.uploadEl.hidden = !this.wantPrint;
      const isService = this.wantPrint && this.printSource === 'service';
      if (this.uploadFilesEl) this.uploadFilesEl.hidden = !this.wantPrint || isService;
      if (this.serviceNoteEl) this.serviceNoteEl.hidden = !isService;
      this.sourceInputs.forEach((el) => {
        el.checked = el.value === (this.printSource || 'upload');
      });
      if (this.printGuideEl) {
        const guideUrl = opt?.printGuideUrl || '';
        if (guideUrl) {
          this.printGuideEl.href = guideUrl;
          this.printGuideEl.hidden = false;
        } else {
          this.printGuideEl.removeAttribute('href');
          this.printGuideEl.hidden = true;
        }
      }

      if (this.printWarning) this.printWarning.hidden = true;

      if (this.fileFrontName) {
        this.fileFrontName.hidden = !this.fileFront;
        this.fileFrontName.textContent = this.fileFront?.name || '';
      }
      if (this.fileBackName) {
        this.fileBackName.hidden = !this.fileBack;
        this.fileBackName.textContent = this.fileBack?.name || '';
      }

      const baseCents = Number(base?.price) || 0;
      const printCents = this.wantPrint ? Number(print?.price) || 0 : 0;
      const totalFormatted = formatMoney(baseCents + printCents);
      if (this.basePriceEl) this.basePriceEl.textContent = moneyOf(base);
      if (this.printRow) {
        this.printRow.classList.toggle('is-added', this.wantPrint);
        if (this.printStateEl) {
          this.printStateEl.textContent = this.wantPrint
            ? this._printBillingMeta(i18n)
            : t(i18n, 'addonNotAdded', 'Eklenmedi');
        }
        if (this.printLine) {
          this.printLine.textContent = this.wantPrint && print ? `+ ${moneyOf(print)}` : '—';
        }
      }
      if (this.totalEl) this.totalEl.textContent = totalFormatted;

      if (base?.id) {
        const input = document.querySelector(`form[action*="/cart/add"] [name="id"]`);
        if (input) {
          input.value = String(base.id);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    _hasAnyPrintFile() {
      return Boolean(this.fileFront || this.fileBack);
    }

    _printBillingMeta(i18n) {
      if (this.printSource === 'service') {
        return t(i18n, 'printSourceServiceValue', 'HarfMix tasarım / baskı hizmeti');
      }
      const both = Boolean(this.fileFront && this.fileBack);
      const one = Boolean((this.fileFront || this.fileBack) && !both);
      if (one) {
        return t(
          i18n,
          'printDualOneSide',
          'Tek yüz yüklendi — çift yüz baskı ücreti uygulanır'
        );
      }
      return t(i18n, 'printOn', 'Görsel baskı seçildi');
    }

    _printSidesSummary(i18n) {
      if (this.fileFront && this.fileBack) {
        return t(i18n, 'printSidesBoth', 'Ön + arka yüz');
      }
      if (this.fileFront) {
        return t(
          i18n,
          'printSidesFrontOnly',
          'Yalnızca ön yüz — 2 adet (çift yüz) ücreti uygulandı'
        );
      }
      if (this.fileBack) {
        return t(
          i18n,
          'printSidesBackOnly',
          'Yalnızca arka yüz — 2 adet (çift yüz) ücreti uygulandı'
        );
      }
      return '';
    }

    async _addToCart() {
      const i18n = this.i18n;
      const { opt, base, print } = this._resolved();
      if (!base?.id || !base.available) {
        this._setStatus(t(i18n, 'variantMissing', 'Varyant bulunamadı.'), 'error');
        return;
      }
      if (this.wantPrint && this.printSource !== 'service' && !this._hasAnyPrintFile()) {
        if (this.printWarning) this.printWarning.hidden = false;
        this._openPrintUpload();
        this._setStatus(
          t(i18n, 'printRequired', 'En az bir yüz için baskı dosyası yükleyin veya HarfMix hizmetini seçin.'),
          'error'
        );
        return;
      }
      if (this.wantPrint && !print?.id) {
        this._setStatus(t(i18n, 'variantMissing', 'Baskı varyantı bulunamadı.'), 'error');
        return;
      }

      const fd = new FormData();
      let index = 0;
      const appendItem = (variantId, props, files) => {
        fd.append(`items[${index}][id]`, String(variantId));
        fd.append(`items[${index}][quantity]`, '1');
        Object.entries(props).forEach(([k, v]) => {
          if (v == null || v === '') return;
          fd.append(`items[${index}][properties][${k}]`, String(v));
        });
        (files || []).forEach(({ key, file }) => {
          if (!file) return;
          fd.append(`items[${index}][properties][${key}]`, file, file.name);
        });
        index += 1;
      };

      const typeLabel = this.productTitle || 'Çift Taraflı Stant Lightbox';
      const baseProps = {
        [t(i18n, 'propType', 'Ürün Tipi')]: typeLabel,
        [t(i18n, 'propSize', 'Ölçü')]: opt?.label || '',
      };
      if (this.printEnabled) {
        baseProps[t(i18n, 'propPrint', 'Görsel Baskı')] = this.wantPrint
          ? t(i18n, 'yes', 'Evet')
          : t(i18n, 'no', 'Hayır');
      }
      appendItem(base.id, baseProps);

      if (this.wantPrint && print?.id) {
        const note = String(this.printNoteEl?.value || this.printNote || '').trim();
        const isService = this.printSource === 'service';
        const printProps = {
          [t(i18n, 'propType', 'Ürün Tipi')]: t(i18n, 'propPrint', 'Görsel Baskı'),
          [t(i18n, 'propSize', 'Ölçü')]: opt?.printArea || '',
          [t(i18n, 'propPrint', 'Görsel Baskı')]: t(i18n, 'yes', 'Evet'),
          [t(i18n, 'propPrintSource', 'Baskı tercihi')]: isService
            ? t(i18n, 'printSourceServiceValue', 'HarfMix tasarım / baskı hizmeti')
            : t(i18n, 'printSourceUploadValue', 'Dosya yükleme'),
          [t(i18n, 'propPrintBilling', 'Baskı ücreti')]: t(
            i18n,
            'printBillingTwo',
            'Çift yüz baskı (ön + arka)'
          ),
          _sb_addon: 'print',
        };
        if (!isService) {
          printProps[t(i18n, 'propPrintSides', 'Yüklenen yüzler')] = this._printSidesSummary(i18n);
        }
        if (note) {
          printProps[t(i18n, 'propPrintNote', 'Baskı notu')] = note;
        }
        appendItem(
          print.id,
          printProps,
          isService
            ? []
            : [
                { key: t(i18n, 'propPrintFront', 'Ön Yüz Baskı'), file: this.fileFront },
                { key: t(i18n, 'propPrintBack', 'Arka Yüz Baskı'), file: this.fileBack },
              ]
        );
      }

      const sectionIds = cartSectionIds();
      if (sectionIds.length) fd.append('sections', sectionIds.join(','));

      this._setStatus(t(i18n, 'adding', 'Ekleniyor…'), 'success');
      try {
        const res = await fetch(cartAddUrl(), {
          method: 'POST',
          body: fd,
          headers: { Accept: 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || t(i18n, 'addError', 'Sepete eklenemedi.'));
        }
        this._setStatus(t(i18n, 'added', 'Sepete eklendi.'), 'success');
        const lines = [{ merchandiseId: String(base.id), quantity: 1 }];
        if (this.wantPrint && print?.id) lines.push({ merchandiseId: String(print.id), quantity: 1 });
        await notifyHorizonCart(data, lines);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data, bubbles: true }));
      } catch (err) {
        console.error('[StandboxCraft]', err);
        this._setStatus(err.message || t(i18n, 'addError', 'Sepete eklenemedi.'), 'error');
      }
    }
  }

  if (!customElements.get('standbox-craft')) {
    customElements.define('standbox-craft', StandboxCraft);
  }
})();
